import React from 'react';
import * as yup from 'yup';
import axios from 'axios';
import {QuickSettingsForm, FormFieldConfig} from '@/components/forms';
import {useAlert} from '@/components/alerts';
import {useStoreActions} from 'easy-peasy';
import {validateConfigurationValue, getConfigurationMetadata, getConfigurationOptions, isConfigurationBoolean, isConfigurationNumber} from '@/utils/configurationValidation';
import i18n from 'i18n';

interface Configuration {
    id: number | null;
    key: string;
    title: string;
    value: string;
    description: string;
    createdOn: string | null;
    updatedOn?: string | null;
    source: 'database' | 'platform_default';
}

interface ConfigurationSettingsFormProps {
    configurationId?: number;
    configurationData?: Configuration | null;
    onSave?: () => void;
}

interface FormValues {
    key: string;
    value: string | { value: string; label: string } | null;
}

const ConfigurationSettingsForm: React.FC<ConfigurationSettingsFormProps> = React.memo(
    ({configurationId = 0, configurationData = null, onSave = () => {}}) => {
        const {showSuccess} = useAlert();
        const setReloadConfigurations = useStoreActions((actions: any) => actions.configurations.setReload);

        const isPlatformDefault = configurationData?.source === 'platform_default';
        const isEditing = configurationId > 0;

        // Get configuration metadata for enhanced validation
        const configMetadata = configurationData?.key ? getConfigurationMetadata(configurationData.key) : null;
        const configOptions = configurationData?.key ? getConfigurationOptions(configurationData.key) : null;
        const isBoolean = configurationData?.key ? isConfigurationBoolean(configurationData.key) : false;
        const isNumber = configurationData?.key ? isConfigurationNumber(configurationData.key) : false;

        const formatDisplayLabel = (value: string): string => {
            if (isBoolean) {
                const boolValue = value.toLowerCase();
                if (boolValue === 'true' || boolValue === '1' || boolValue === 'yes' || boolValue === 'on') {
                    return i18n.t('jt_true.text');
                }
                return i18n.t('jt_false.text');
            }

            // Specific translations for period dropdown values
            if (value === 'this_month') {
                return i18n.t('dd_period_thismonth.text');
            }
            if (value === 'this_week') {
                return i18n.t('dd_period_thisweek.text');
            }
            if (value === 'this_day') {
                return i18n.t('dd_period_thisday.text');
            }

            if (configOptions && configOptions.includes(value)) {
                const parts = value.split('_');
                if (!parts.length) {
                    return value;
                }

                return parts
                    .map((word, index) =>
                        index === 0
                            ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                            : word.toLowerCase()
                    )
                    .join(' ');
            }

            return value;
        };

        // Convert initial value for select fields
        const getInitialValue = (): string | {value: string; label: string} | null => {
            const rawValue = configurationData?.value || '';

            // For select fields (boolean or options), convert string to object format
            if (isBoolean || configOptions) {
                if (rawValue) {
                    return {value: rawValue, label: formatDisplayLabel(rawValue)};
                }
                return null;
            }

            return rawValue;
        };

        const initialValues: FormValues = {
            key: configurationData?.key || '',
            value: getInitialValue(),
        };


        const customSuccessHandling = (response: any, actionName: string) => {
            if (configurationId) {
                showSuccess(i18n.t('jt_configuration_updated_successfully.text'));
            } else {
                showSuccess(i18n.t('jt_configuration_created_successfully.text'));
            }
            setReloadConfigurations(true);
            onSave();
        };

        // Custom submission handler to transform values
        const customSubmissionHandler = async (values: FormValues, formik: any) => {
            const transformedValues = {...values};

            // Convert select field values back to strings for API
            if (transformedValues.value && typeof transformedValues.value === 'object' && 'value' in transformedValues.value) {
                transformedValues.value = transformedValues.value.value;
            }

            // Handle null values
            if (transformedValues.value === null) {
                transformedValues.value = '';
            }

            // Determine request method and URL
            const resourceUrl = window.URLS.configurationsResource;
            let requestMethod: any;
            let url = resourceUrl;

            if (configurationId && !isPlatformDefault) {
                url = `${url}/${configurationId}`;
                requestMethod = (url: string, data: any) => axios.put(url, data);
            } else {
                requestMethod = (url: string, data: any) => axios.post(url, data);
            }

            return await requestMethod(url, transformedValues);
        };

        const fields: FormFieldConfig[] = [
            {
                name: 'key',
                label: i18n.t('jt_configuration_key.text'),
                type: 'text',
                required: true,
                disabled: isEditing || isPlatformDefault, // Read-only when editing or overriding platform default
                validation: yup
                    .string()
                    .max(255, i18n.t('jt_key_max_255_characters.text'))
                    .required(i18n.t('jt_key_is_required.text')),
            },
            {
                name: 'value',
                label: i18n.t('jt_value.text'),
                type: isBoolean ? 'select' :
                    configOptions ? 'select' :
                        isNumber ? 'number' : 'textarea',
                required: false,
                options: isBoolean
                    ? [
                        {value: 'true', label: i18n.t('jt_true.text')},
                        {value: 'false', label: i18n.t('jt_false.text')}
                    ]
                    : configOptions
                        ? configOptions.map((opt: string) => ({value: opt, label: formatDisplayLabel(opt)}))
                        : undefined,
                validation: yup
                    .mixed()
                    .test('configuration-validation', i18n.t('jt_invalid_configuration_value.text'), function (value: any) {
                        if (!configurationData?.key) {return true;}

                        // Handle select field values (objects with value/label)
                        let actualValue: string | number | boolean = value;
                        if (value && typeof value === 'object' && 'value' in value) {
                            actualValue = (value as {value: string; label: string}).value;
                        }

                        // Allow empty values
                        if (!actualValue && actualValue !== 0 && actualValue !== false) {return true;}

                        const result = validateConfigurationValue(configurationData.key, String(actualValue));
                        if (!result.isValid) {
                            return this.createError({message: result.errorMessage});
                        }
                        return true;
                    }),
                helpText: configurationData?.description || undefined,
            },
        ];

        return (
            <QuickSettingsForm
                entityId={isPlatformDefault ? undefined : (configurationId || undefined)}
                crudConfig={{
                    resourceUrl: window.URLS.configurationsResource,
                    storeActionPath: 'configurations.addOrEdit',
                    successMessageKey: 'jt_configuration_changed.text',
                    entityName: 'configuration',
                }}
                fields={fields}
                initialValues={initialValues}
                onSave={onSave}
                customSuccessHandling={customSuccessHandling}
                customSubmissionHandler={customSubmissionHandler}
            />
        );
    }
);

export default ConfigurationSettingsForm;

